import {type Draft, produce} from 'immer';

import type {BaseSearchHistoryItem} from '../types';
import {isRunSearchHistoryItem} from './SearchHistory';

const MAX_ITEMS_IN_HISTORY = 20;

export const produceSearchHistory = <T extends BaseSearchHistoryItem>(
  newQuery: string,
  currentSearchHistory: T[] | undefined,
  maxItemsInHistory = MAX_ITEMS_IN_HISTORY,
  isRegex?: boolean
): T[] | undefined => {
  if (!newQuery.trim() || newQuery.length > 1000) {
    return currentSearchHistory;
  }

  const newHistoryItem = (
    isRegex !== undefined ? {query: newQuery, isRegex} : {query: newQuery}
  ) as T;

  if (!currentSearchHistory) {
    return isRunSearchHistoryItem(newHistoryItem)
      ? [newHistoryItem]
      : [newHistoryItem];
  }
  // look for history first
  // note that a race condition is possible
  const searchHistory = produce(currentSearchHistory, draftHistory => {
    const matchIdx = draftHistory.findIndex(item => item.query === newQuery);

    if (matchIdx === 0) {
      // If the first item in the history has a new regex value but the same key.
      // e.g. we're updating {query: 'mycoolsearch', isRegex: true} to {query: 'mycoolsearch', isRegex: false}.
      // Let's not store duplicate queries with different regex values.
      if ((draftHistory[0] as any).isRegex === isRegex) {
        return;
      }
      draftHistory[0] = newHistoryItem as Draft<T>;
      return;
    }

    if (matchIdx > 0) {
      // remove existing item from the list
      draftHistory.splice(matchIdx, 1);
    }

    if (draftHistory.length >= maxItemsInHistory) {
      draftHistory.pop();
    }

    // add new item to beginning of list
    draftHistory.unshift(newHistoryItem as Draft<T>);
  });

  return searchHistory;
};
